import React, { memo } from 'react';

import { makeStyles } from '@styles/makeStyles';
import { Box, Divider, Drawer, List } from '@mui/material';
import IconButton from '@components/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import { useSetDrawerState } from '@common/components/navigation-drawer/drawer-state-context';
import EduphoriaLogo from '@common/icons/logo-eduphoria.svg';

const useStyles = makeStyles()((theme, _params, classes) => ({
  drawerRoot: {
    overflowY: 'hidden',
    flex: 'none',
    paddingTop: theme.spacing(0.5),
    '@media print': { display: 'none' }
  },
  drawerPaper: {
    position: 'relative',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.navigationDrawer.background.base,
    width: theme.mixins.navigationDrawer.openWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflowX: 'hidden'
  },
  drawerPaperClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    width: theme.mixins.navigationDrawer.closedWidth
  },
  listRoot: {
    overflowY: 'auto',
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    [`&.${classes.listDrawerClosed}`]: {
      padding: theme.spacing(1)
    }
  },
  eduphoriaLogo: {
    '& path': { fill: `${theme.palette.grey[600]} !important` }
  }
}));

const NavigationDrawer = ({ isDrawerOpen, children }) => {
  const { classes, cx } = useStyles();

  const setDrawerState = useSetDrawerState({ userIntent: true });

  const navigationDrawerId = 'eduNavigationDrawer';

  return (
    <Drawer
      variant="permanent"
      role="navigation"
      aria-label="navigation drawer"
      id={navigationDrawerId}
      classes={{
        root: classes.drawerRoot,
        paper: cx(classes.drawerPaper, !isDrawerOpen && classes.drawerPaperClose)
      }}
      open={isDrawerOpen}
    >
      <List
        component="nav"
        disablePadding
        classes={{
          root: cx(classes.listRoot, !isDrawerOpen && classes.listDrawerClosed)
        }}
      >
        {children}
      </List>
      <Box sx={{ textAlign: isDrawerOpen ? 'right' : 'center' }}>
        <Divider sx={{ backgroundColor: 'navigationDrawer.dividerColor' }} />

        {isDrawerOpen && (
          <Box
            className={classes.eduphoriaLogo}
            sx={{
              position: 'absolute',
              mt: 1.75,
              width: 140,
              '& svg': { height: '1.5rem' }
            }}
          >
            <EduphoriaLogo />
          </Box>
        )}

        <IconButton
          sx={theme => ({
            transform: !isDrawerOpen && 'rotate(180deg)',
            transition: theme.transitions.create('transform', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.short
            }),
            ml: 1,
            color: 'navigationDrawer.icon.base'
          })}
          onClick={() => setDrawerState(!isDrawerOpen)}
          aria-controls={navigationDrawerId}
          aria-expanded={isDrawerOpen}
          aria-label="toggle navigation drawer"
          size="large"
        >
          <ArrowBackIosNewIcon />
        </IconButton>
      </Box>
    </Drawer>
  );
};

export default memo(NavigationDrawer);
